import React from 'react'
import {
  AboutNavigation,
  AchievementsGrid,
  ContactSection,
  IntroSection,
  JourneyTimeline,
  PhilosophyGrid,
  SkillsSection,
} from '../components/about'
import { SEOHead } from '../components/seo/SEOHead'
import { Container, Heading, Text } from '../components/ui'
import '../styles/AboutPage.css'

/**
 * About page component showcasing Lucas Stufflebeam's personal story,
 * technical philosophy, and professional journey in first person.
 *
 * Features:
 * - Modular component architecture with proper separation of concerns
 * - Smooth scrolling navigation between sections
 * - Comprehensive technical skills showcase
 * - Professional career timeline
 * - Quantified achievements and metrics
 * - Contact information and availability
 * - Full accessibility compliance with WCAG 2.1 AA
 * - Responsive design across all device sizes
 * - SEO-optimized with structured data and semantic HTML
 */
const AboutPage: React.FC = () => {
  return (
    <>
      {/* SEO Meta Tags for About Page */}
      <SEOHead page="about" path="/about" />

      <main
        className="page-main"
        id="main-content"
        tabIndex={-1}
        role="main"
        aria-label="About Lucas Stufflebeam"
      >
        <Container size="lg" padding="lg">
          {/* Skip to content and smooth scrolling navigation */}
          <AboutNavigation />

          {/* Page Header */}
          <header className="about-header">
            <Heading
              as="h1"
              size="3xl"
              variant="gradient"
              aria-label="About Lucas Stufflebeam, Principal Software Architect"
            >
              <span className="about-title-part">About </span>
              <span className="about-name-part">Lucas Stufflebeam</span>
            </Heading>
            <Text size="lg" variant="secondary">
              Principal Software Architect | Scalable Game Backends |
              Cloud-Native | AI DevOps | Kubernetes
            </Text>
          </header>

          <div className="about-content">
            {/* Personal Introduction */}
            <IntroSection id="intro" />

            {/* Technical Skills & Expertise */}
            <SkillsSection id="skills" />

            {/* Technical Philosophy */}
            <PhilosophyGrid id="philosophy" />

            {/* Career Journey Timeline */}
            <JourneyTimeline id="journey" />

            {/* Key Achievements */}
            <AchievementsGrid id="achievements" />

            {/* Contact & Availability */}
            <ContactSection id="contact" />
          </div>
        </Container>
      </main>
    </>
  )
}

export default AboutPage
